/// <amd-module name="Widgets/SprintOverviewWidget/SprintOverviewWidget" />
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import { getProject } from "Tfs/Widget/Context";
import { VssComponent } from "VSS/Platform/Layout";

import { getUtcDate } from "Agile/Common/Utils/Date";
import { TeamSetting, TeamSettingsIteration } from "Dashboards/Clients/Contracts/Work";
import { VisualTitle } from "Reporting/Visuals/VisualTitle";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IVssLocationService } from "VSS/Platform/Location";
import { getSetIterationsMessage } from "WidgetCommon/IterationDatesMessage/SetIterationDatesMessage";
import { getCoreClient } from "Widgets/SprintOverviewWidget/clients/Core";
import * as Resources from "Widgets/SprintOverviewWidget/Resources";
import { NColorBarControl, NColorBarCounts } from "./NColorBarControl";
import * as SprintOverviewDataProvider from "./SprintOverviewDataProvider";
import { WorkItemRollupValue } from "./SprintOverviewDataProvider";

export interface ISprintOverviewWidgetState {
    widgetSettings: SprintOverviewWidgetSettings;
    teamSettings: TeamSetting;
    /**
     * Describes the number of days remaining in the iteration. Calculated conditionally based on includeDaysOff.
     */
    daysRemaining: number;
    /**
     * Describes the number of days in the iteration. Calculated conditionally based on includeDaysOff.
     */
    daysInIteration: number;
    workItemsDone: WorkItemRollupValue;
    workItemsInProgress: WorkItemRollupValue;
    workItemsNotStarted: WorkItemRollupValue;
    showSetIterationDatesMessage: boolean;
}

export interface Team {
    /**
     * identifier of the team
     */
    id: string;
    /**
     * Name of the team
     */
    name: string;
}

export interface SprintOverviewWidgetSettings {
    /**
     * The Actual team data
     */
    team: Team;
    /**
     * The actual selected units
     */
    units: string;
    /**
     * Enable and disable weekends
     */
    includeDaysOff: boolean;
}

export class SprintOverviewWidget extends VssComponent<WidgetProps, ISprintOverviewWidgetState> {
    public static readonly componentType = "sprintOverviewWidget";
    public static readonly workDaysStatusBarSize = 310;

    public render(): JSX.Element | null {
        if (this.state) {
            if (this.state.showSetIterationDatesMessage) {
                return getSetIterationsMessage(this.context.pageContext, this.props.name, this.props.size);
            } else {
                return (
                    <FPSLink
                        href={this.getBacklogUrl()}
                        target={this.props.openLinksInNewTab ? "_blank" : undefined}
                        className="sprint-overview-widget-wrapper"
                    >
                        <div className="widget-sprint-overview">
                            {this.renderTitle(this.state.widgetSettings.team, this.state.teamSettings.defaultIteration)}
                            {this.renderWorkDaysStatusBar()}
                            {this.renderWorkItemRollupStatusBar()}
                        </div>
                    </FPSLink>
                );
            }
        } else {
            return null;
        }
    }

    public async componentDidMount(): Promise<void> {
        await this.getWidgetData();
        WidgetTelemetry.onWidgetLoaded(this.context.pageContext, this.props);
    }

    public async componentDidUpdate(prevProps: WidgetProps, prevState: ISprintOverviewWidgetState): Promise<void> {
        if (prevProps.settings !== this.props.settings) {
            await this.getWidgetData();
        }
    }

    // Exposed to simplify testing patterns
    public getDaysRemainingText(daysRemaining: number): string {
        if (daysRemaining === 0) {
            return Resources.NoDayRemaining;
        } else if (daysRemaining === 1) {
            return Resources.DayRemaining;
        } else {
            return format(Resources.DaysRemaining, daysRemaining);
        }
    }

    // Exposed to simplify testing patterns
    public getDaysCompletedText(daysCompleted: number): string {
        if (daysCompleted === 1) {
            return Resources.DayCompleted;
        } else {
            return format(Resources.DaysCompleted, daysCompleted);
        }
    }

    public getWorkRemainingText(
        units: string,
        proposed: WorkItemRollupValue,
        inProgress: WorkItemRollupValue,
        completed: WorkItemRollupValue
    ): string {
        const countOfWorkStarted = inProgress.count + completed.count;
        if (units == Resources.CountOfWorkItems) {
            if (proposed.count === 0) {
                return countOfWorkStarted === 0 ? Resources.WorkNotStarted : "";
            } else if (proposed.count === 1) {
                return Resources.WorkRemaining;
            } else {
                return format(Resources.WorksRemaining, proposed.count);
            }
        } else {
            if (proposed.effort <= 0) {
                return countOfWorkStarted === 0 ? Resources.WorkNotStarted : "";
            } else {
                return format(Resources.EffortUnitsNotStarted, proposed.effort, units.toLowerCase());
            }
        }
    }

    private async getWidgetData(): Promise<void> {
        const widgetSettings: SprintOverviewWidgetSettings = await this.getWidgetSettings(this.props);
        const teamContext = this.getTeamContext(widgetSettings);
        let sprintData;
        let shortCircuitAndSetIterationMessage = false;
        try {
            sprintData = await SprintOverviewDataProvider.getIterationInfo(this.context.pageContext, teamContext);
            shortCircuitAndSetIterationMessage = !(
                sprintData.teamSettings.defaultIteration.attributes.startDate && sprintData.teamSettings.defaultIteration.attributes.finishDate
            );
        } catch (error) {
            shortCircuitAndSetIterationMessage = true;
        }
        if (shortCircuitAndSetIterationMessage || !sprintData) {
            // If initial data request fails it is due to an issue with iteration setttings so we should direct users to the setIterations page.
            this.setState({ showSetIterationDatesMessage: true }, this.props.onWidgetLoaded);
            return;
        }
        const workItemRefs = await SprintOverviewDataProvider.getIterationWorkItemIds(
            this.context.pageContext,
            teamContext,
            sprintData.teamSettings.defaultIteration
        );
        const [workItemRollup, iterationTimeframe] = await Promise.all([
            // Parallel execution to reduce wait time
            SprintOverviewDataProvider.getIterationWorkRollup(
                this.context.pageContext,
                workItemRefs.workItemRelations.map<number>(x => x.target.id),
                sprintData.backlogConfiguration,
                sprintData.processConfiguration
            ),
            SprintOverviewDataProvider.getIterationTimeframe(
                this.context.pageContext,
                teamContext,
                sprintData.teamSettings.defaultIteration,
                sprintData.teamSettings.workingDays
            )
        ]);

        const remainingDaysInIteration = widgetSettings.includeDaysOff
            ? iterationTimeframe.remainingDaysInIteration
            : iterationTimeframe.remainingWorkDaysInIteration;
        const totalDaysInIteration = widgetSettings.includeDaysOff
            ? iterationTimeframe.totalDaysInIteration
            : iterationTimeframe.totalWorkDaysInIteration;

        this.setState(
            {
                teamSettings: sprintData.teamSettings,
                daysRemaining: remainingDaysInIteration,
                daysInIteration: totalDaysInIteration,
                workItemsDone: workItemRollup.complete,
                workItemsInProgress: workItemRollup.inProgress,
                workItemsNotStarted: workItemRollup.notStarted,
                widgetSettings
            },
            this.props.onWidgetLoaded
        );
    }

    private getBacklogUrl(): string {
        const locationService = this.context.pageContext.getService<IVssLocationService>("IVssLocationService");
        const teamContext = this.getTeamContext(this.state.widgetSettings);
        return locationService.routeUrl("ms.vss-work-web.backlogs-route", {
            project: teamContext.project,
            team: teamContext.team
        });
    }

    private renderTitle(team: Team, iteration?: TeamSettingsIteration): JSX.Element {
        let sprintData: JSX.Element | null = null;
        if (iteration) {
            const startDate = getUtcDate(iteration.attributes.startDate);
            const finishDate = getUtcDate(iteration.attributes.finishDate);

            sprintData = (
                <div className="sprint-iteration">
                    <div className="truncated text-ellipsis">{iteration.name}</div>
                    <div className="sprint-iteration date-range">{this.getDateRange(startDate, finishDate)}</div>
                </div>
            );
        }
        return (
            <div>
                <div className="truncated text-ellipsis team-title">
                    <VisualTitle text={team.name} suppressDefaultPadding />
                </div>
                {sprintData}
            </div>
        );
    }

    private getDateRange(start: Date, finish: Date): string {
        const startDate = start.toLocaleDateString(undefined, { month: "long", day: "numeric" });
        const finishDate = finish.toLocaleDateString(undefined, { month: "long", day: "numeric" });
        return format(Resources.DateRangeFormatString, startDate, finishDate);
    }

    private async getWidgetSettings(props: WidgetProps): Promise<SprintOverviewWidgetSettings> {
        const currentTeam = props.teamContext;
        if (props.settings) {
            const settings: SprintOverviewWidgetSettings = JSON.parse(props.settings);
            if (settings.team.id === currentTeam.id) {
                settings.team = currentTeam;
            } else {
                const teamsService = getCoreClient(this.context.pageContext);
                const project = getProject(this.context.pageContext);
                const team = await teamsService.getTeam(project.id, settings.team.id);
                settings.team = team;
            }
            return settings;
        } else {
            return {
                includeDaysOff: false,
                units: Resources.CountOfWorkItems,
                team: currentTeam
            };
        }
    }

    private renderWorkDaysStatusBar(): JSX.Element {
        const daysBarOptions = this.getDaysBarOptions(this.state.daysInIteration, this.state.daysRemaining);
        const description = this.getDaysRemainingText(this.state.daysRemaining);
        return (
            <>
                <div className="days-remaining truncated text-ellipsis progress-day-bar-description">
                    {description}
                </div>
                <NColorBarControl counts={daysBarOptions} size={SprintOverviewWidget.workDaysStatusBarSize} />
            </>
        );
    }

    private renderWorkItemRollupStatusBar(): JSX.Element {
        const { widgetSettings, workItemsDone, workItemsInProgress, workItemsNotStarted } = this.state;
        const text = this.getWorkRemainingText(widgetSettings.units, workItemsNotStarted, workItemsInProgress, workItemsDone);
        const workBarOptions = this.getWorkBarOptions(widgetSettings.units, workItemsNotStarted, workItemsInProgress, workItemsDone);

        return (
            <>
                <div className="progress-work-bar-description truncated text-ellipsis user-story-progress" aria-label={text}>
                    {text}
                </div>
                <NColorBarControl counts={workBarOptions} size={SprintOverviewWidget.workDaysStatusBarSize} />
            </>
        );
    }

    private getWorkBarOptions(
        units: string,
        proposed: WorkItemRollupValue,
        inProgress: WorkItemRollupValue,
        completed: WorkItemRollupValue
    ): NColorBarCounts[] {
        let completedValue: number;
        let inProgressValue: number;
        let proposedValue: number;

        if (units == Resources.CountOfWorkItems) {
            // check the units we want and read data
            completedValue = completed.count;
            inProgressValue = inProgress.count;
            proposedValue = proposed.count;
        } else {
            completedValue = completed.effort;
            inProgressValue = inProgress.effort;
            proposedValue = proposed.effort;
        }

        const completedHover = format(Resources.Completed, completedValue);
        const inProgressHover = format(Resources.InProgress, inProgressValue);
        const proposedHover = format(Resources.NotStarted, proposedValue);
        const days: Array<NColorBarCounts> = [];
        days.push({
            count: completedValue,
            hover: completedHover,
            color: "green"
        });
        days.push({
            count: inProgressValue,
            hover: inProgressHover,
            color: "blue"
        });
        days.push({
            count: proposedValue,
            hover: proposedHover,
            color: "gray"
        });

        return days;
    }

    private getDaysBarOptions(workingDays: number, daysRemaining: number): NColorBarCounts[] {
        const days: Array<NColorBarCounts> = [];
        const daysCompleted = Math.max(workingDays - daysRemaining, 0);
        const completedHover = this.getDaysCompletedText(daysCompleted);
        const remainingHover = this.getDaysRemainingText(daysRemaining);
        days.push({
            count: daysCompleted,
            hover: completedHover,
            color: "purple"
        });
        days.push({
            count: daysRemaining,
            hover: remainingHover,
            color: "gray"
        });

        return days;
    }

    private getTeamContext(settings: SprintOverviewWidgetSettings): TeamContext {
        const project = getProject(this.context.pageContext);
        const team = settings.team;
        return {
            projectId: project.id,
            project: project.name,
            teamId: team.id,
            team: team.name
        };
    }
}

VssComponent.register(SprintOverviewWidget.componentType, SprintOverviewWidget);
